import React, { useState, useEffect } from 'react';
import * as Select from '@radix-ui/react-select';
import { ChevronDown, AlertTriangle, Shield, TrendingDown, Leaf, Clock } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { SignalDetail } from './SignalDetail';

interface Signal {
  id: number;
  title: string;
  severity: string;
  category: string;
  signal_type: string;
  description: string;
  detected_at: string;
  article_published_at?: string;
  corroboration?: number;
  sentiment?: number;
  maldives_weight?: number;
  source_tier?: number;
  metadata?: Record<string, any>;
  tags?: { slug: string; label: string; type: string }[];
}

const TYPE_ICON: Record<string, React.ReactNode> = {
  security: <Shield size={14} strokeWidth={1.75} />,
  economic: <TrendingDown size={14} strokeWidth={1.75} />,
  environmental: <Leaf size={14} strokeWidth={1.75} />,
  political: <AlertTriangle size={14} strokeWidth={1.75} />,
};

// Radix Select.Item disallows an empty-string value, so use a sentinel for "all".
const ALL = 'all';

// Maldives Standard Time is UTC+5, IANA zone Indian/Maldives (no DST).
function toMVT(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: 'Indian/Maldives',
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function getMVTClock(): string {
  return new Date().toLocaleTimeString('en-GB', {
    timeZone: 'Indian/Maldives',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }) + ' MVT';
}

// Card accent is determined by signal category, not severity.
// Priority: Breaking (critical + recent) > Maldives news > International
function isBreaking(sig: Signal): boolean {
  if (sig.severity !== 'critical') return false;
  const ageMs = Date.now() - new Date(sig.detected_at).getTime();
  return ageMs < 6 * 60 * 60 * 1000; // within last 6 hours
}

function isMaldivesNews(sig: Signal): boolean {
  if (sig.maldives_weight && sig.maldives_weight > 1.0) return true;
  return sig.tags?.some((t) => t.slug === 'maldives') ?? false;
}

function getCardAccent(sig: Signal): string {
  if (isBreaking(sig)) return 'danger';
  if (isMaldivesNews(sig)) return 'success';
  return 'info';
}

// Severity chip still shows the raw severity level for analytical use.
const SEVERITY_TONE: Record<string, string> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'success',
};

function getSourceName(sig: Signal): string | null {
  if (sig.metadata?.source_name) return sig.metadata.source_name as string;
  const parts = sig.title.split(' - ');
  return parts.length > 1 ? parts[parts.length - 1] : null;
}

function getTierLabel(tier?: number): string {
  if (!tier) return '';
  if (tier <= 1) return 'Tier 1';
  if (tier <= 2) return 'Tier 2';
  return 'Tier 3';
}

function getTierTone(tier?: number): string {
  if (!tier) return 'neutral';
  if (tier <= 1) return 'warning';
  if (tier <= 2) return 'info';
  return 'success';
}

function getSentimentTone(sentiment?: number): string {
  if (!sentiment) return 'neutral';
  if (sentiment < -0.5) return 'danger';
  if (sentiment < -0.1) return 'warning';
  if (sentiment > 0.5) return 'success';
  return 'info';
}

export function SignalBrowser() {
  const [days, setDays] = useState<number | null>(7);
  const [severity, setSeverity] = useState(ALL);
  const [limit, setLimit] = useState(50);
  const [openId, setOpenId] = useState<number | null>(null);
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [mvtClock, setMvtClock] = useState(getMVTClock);

  useEffect(() => {
    const id = setInterval(() => setMvtClock(getMVTClock()), 1000);
    return () => clearInterval(id);
  }, []);

  const params = new URLSearchParams({ limit: String(limit) });
  if (days !== null) params.set('days', String(days));
  if (severity !== ALL) params.set('severity', severity);
  if (tagFilter) params.set('tag', tagFilter);

  const { data, loading } = useApi<{ signals: Signal[]; count: number; limit: number }>(
    `/api/signals?${params}`,
    [days, severity, limit, tagFilter]
  );

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <span className="eyebrow">Signal Intelligence</span>
          <h1 className="t-headline-md" style={{ marginTop: 'var(--space-2)' }}>Signal Browser</h1>
        </div>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.8rem',
            color: 'var(--color-text-muted)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Clock size={13} strokeWidth={1.75} />
          {mvtClock}
        </span>
      </div>

      {/* Legend */}
      <div className="row" style={{ gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <span className="chip" data-tone="danger" style={{ pointerEvents: 'none' }}>🔴 Breaking</span>
        <span className="chip" data-tone="success" style={{ pointerEvents: 'none' }}>🟢 Maldives</span>
        <span className="chip" data-tone="info" style={{ pointerEvents: 'none' }}>🔵 International</span>
      </div>

      {/* Filters */}
      <div className="row" style={{ gap: 'var(--space-3)' }}>
        <div className="tabs">
          {[{ label: '7d', v: 7 }, { label: '30d', v: 30 }, { label: '90d', v: 90 }, { label: 'All', v: null }].map((opt) => (
            <button
              key={opt.label}
              className={`tab${days === opt.v ? ' is-active' : ''}`}
              onClick={() => { setDays(opt.v); setLimit(50); }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <Select.Root value={severity} onValueChange={setSeverity}>
          <Select.Trigger
            className="btn btn-secondary"
            style={{ minWidth: 148, justifyContent: 'space-between' }}
            aria-label="Filter by severity"
          >
            <Select.Value placeholder="All severities" />
            <Select.Icon><ChevronDown size={14} strokeWidth={1.75} /></Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content className="halo-select-content" position="popper" sideOffset={6}>
              <Select.Viewport>
                <Select.Item value={ALL} className="halo-select-item">
                  <Select.ItemText>All severities</Select.ItemText>
                </Select.Item>
                {['critical', 'high', 'medium', 'low'].map((s) => (
                  <Select.Item key={s} value={s} className="halo-select-item">
                    <Select.ItemText>{s.charAt(0).toUpperCase() + s.slice(1)}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>

        {tagFilter && (
          <button
            className="chip"
            data-tone="info"
            onClick={() => { setTagFilter(null); setLimit(50); }}
            style={{ cursor: 'pointer' }}
            title="Clear tag filter"
          >
            #{tagFilter} ✕
          </button>
        )}

        {data && (
          <span className="chip" data-tone="neutral" style={{ marginLeft: 'auto' }}>
            {data.count} signal{data.count !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Signal list */}
      {loading && (
        <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>Scanning signals…</p>
      )}

      {data && data.signals.length === 0 && (
        <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>No signals found for this filter.</p>
      )}

      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        {data?.signals.map((sig) => {
          const accent = getCardAccent(sig);
          const severityTone = SEVERITY_TONE[sig.severity] || 'neutral';
          const tierTone = getTierTone(sig.source_tier);
          const sentimentTone = getSentimentTone(sig.sentiment);
          const displayTime = sig.article_published_at || sig.detected_at;

          return (
            <div
              key={sig.id}
              className="card card-accent"
              data-accent={accent}
              onClick={() => setOpenId(sig.id)}
              style={{ cursor: 'pointer', padding: 'var(--space-3) var(--space-4)' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', flex: 1 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--text-body-md-size)', color: 'var(--color-text-primary)' }}>
                    {sig.title}
                  </div>
                  <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                    <span className="chip" data-tone={severityTone}>{sig.severity}</span>
                    <span className="chip" data-tone="neutral" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {TYPE_ICON[sig.signal_type]}
                      {sig.signal_type}
                    </span>
                    {sig.source_tier && (
                      <span className="chip" data-tone={tierTone}>{getTierLabel(sig.source_tier)}</span>
                    )}
                    {sig.corroboration && sig.corroboration > 1 && (
                      <span className="chip" data-tone="info">Corroborated ×{sig.corroboration}</span>
                    )}
                    {sig.sentiment !== undefined && sig.sentiment !== null && (
                      <span className="chip" data-tone={sentimentTone}>
                        {sig.sentiment < 0 ? '📉' : '📈'} {(sig.sentiment * 100).toFixed(0)}%
                      </span>
                    )}
                    {sig.maldives_weight && sig.maldives_weight > 1.0 && (
                      <span className="chip" data-tone="warning">🇲🇻 {(sig.maldives_weight * 100).toFixed(0)}%</span>
                    )}
                  </div>
                </div>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.7rem',
                    color: 'var(--color-text-muted)',
                    flexShrink: 0,
                    textAlign: 'right',
                    lineHeight: 1.4,
                  }}
                >
                  {toMVT(displayTime)}
                </span>
              </div>

              {/* Source attribution */}
              {(() => {
                const sourceName = getSourceName(sig);
                const sourceUrl = sig.metadata?.source_url as string | undefined;
                if (!sourceName) return null;
                return (
                  <div style={{ marginTop: 'var(--space-1)' }}>
                    {sourceUrl ? (
                      <a
                        href={sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="t-body-sm"
                        style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}
                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                      >
                        ↗ {sourceName}
                      </a>
                    ) : (
                      <span className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>
                        {sourceName}
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Tags */}
              {sig.tags && sig.tags.length > 0 && (
                <div className="row" style={{ gap: 4, flexWrap: 'wrap', marginTop: 'var(--space-1)' }}>
                  {sig.tags.slice(0, 6).map((t) => (
                    <button
                      key={t.slug}
                      className="chip"
                      data-tone="neutral"
                      onClick={(e) => { e.stopPropagation(); setTagFilter(t.slug); setLimit(50); }}
                      style={{ cursor: 'pointer' }}
                      title={`Filter by ${t.label}`}
                    >
                      #{t.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data && data.count >= limit && (
        <button
          className="btn btn-secondary"
          onClick={() => setLimit((l) => l + 50)}
          style={{ alignSelf: 'center' }}
        >
          Load more
        </button>
      )}

      {openId !== null && <SignalDetail signalId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
